import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ProjectSidebar from '../../components/ProjectSidebar';
import Topbar from '../main/Topbar';
import { formatProjectNameWithType } from '../../utils/project';
import {
  getProjectFeatureLabel,
  orderProjectFeatureCodes,
  PROJECT_FEATURE_OPTIONS,
} from '../../utils/projectConfig';
import { getShopStatusCount } from '../../utils/shop';
import { getProductStatusCount } from '../../utils/product';
import { 
  Smartphone, 
  Menu, 
  Bell, 
  Home,
  User, 
  Users, 
  Briefcase, 
  AlertCircle,
  CheckCircle,
  Clock,
  Coffee,
  ShoppingBag,
  Utensils,
  TrendingUp,
  Settings,
  LogOut,
  ChevronDown,
  ChevronRight,
  DollarSign,
  FileText,
  Edit3,
  ExternalLink,
  Package,
  Store,
  Activity,
  MessageSquare,
  HelpCircle,
  ArrowUp,
  ArrowDown,
  Minus
} from 'lucide-react';

// Types
interface ProjectDetail {
  id: string;
  code: string;
  name: string;
  projectType?: string;
  description: string;
  status: 'ACTIVE' | 'INACTIVE' | 'PENDING' | 'ARCHIVED';
  createdAt: string;
  logo?: string;
  featureCodes: string[];
  earnMethod?: string;
  defaultBrandCode?: string;
}

interface StoreInfo {
  count: number;
  ACTIVE: number;
  INACTIVE: number;
}

interface ProductInfo {
  count: number;
  ACTIVE: number;
  INACTIVE: number;
}

interface MembershipInfo {
  totalMembers: number;
  activeMembers: number;
  vipMembers: number;
  monthlyGrowth: number;
}

interface QnaItem {
  id: string;
  title: string;
  createdAt: string;
  status: 'PENDING' | 'answered';
  priority: 'low' | 'medium' | 'high';
}

interface UserInfo {
  accountId: string;
  userName: string;
  userId: string;
  role: string;
}

const ProjectDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState<boolean>(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState<boolean>(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState<boolean>(false);
  const [activeMenu, setActiveMenu] = useState<string>('overview');
  const [storeInfo, setStoreInfo] = useState<StoreInfo>({
    count: 0,
    ACTIVE: 0,
    INACTIVE: 0,
  });
  const [productInfo, setProductInfo] = useState<ProductInfo>({
    count: 0,
    ACTIVE: 0,
    INACTIVE: 0,
  });

  // Check if screen is mobile size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileSidebarOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  const { projectCode } = useParams<{ projectCode: string }>();
  
  // 프로젝트 정보를 동적으로 설정
  const [projectDetail, setProjectDetail] = useState<ProjectDetail>(() => {
    // localStorage에서 프로젝트 정보를 가져오기
    const savedProject = localStorage.getItem('selectedProject');
    if (savedProject) {
      const parsedProject = JSON.parse(savedProject);
      return {
        id: parsedProject.id,
        code: parsedProject.code,
        name: parsedProject.name,
        projectType: parsedProject.projectType ?? parsedProject.type,
        earnMethod:
          typeof parsedProject.earnMethod === 'string'
            ? parsedProject.earnMethod.trim() || undefined
            : undefined,
        defaultBrandCode:
          typeof parsedProject.defaultBrandCode === 'string'
            ? parsedProject.defaultBrandCode.trim() || undefined
            : undefined,
        description: parsedProject.description,
        status: parsedProject.status,
        createdAt: parsedProject.createdAt,
        logo: parsedProject.logo,
        featureCodes: Array.isArray(parsedProject.featureCodes) ? parsedProject.featureCodes : [],
      };
    }
    
    // 기본값
    return {
      id: 'PRJ-001',
      code: projectCode || 'GANGNAM-001',
      name: '강남점',
      projectType: undefined,
      description: '강남역 1번 출구 플래그십 스토어',
      status: 'ACTIVE',
      createdAt: '2025-01-10',
      logo: undefined,
      featureCodes: []
    };
  });

  const featureCodes = orderProjectFeatureCodes(projectDetail.featureCodes ?? []);

  useEffect(() => {
    const targetProjectCode = projectCode || projectDetail.code;
    if (!targetProjectCode) {
      setStoreInfo({ count: 0, ACTIVE: 0, INACTIVE: 0 });
      return;
    }

    getShopStatusCount(targetProjectCode)
      .then((stats) => {
        setStoreInfo(stats);
      })
      .catch((err) => {
        console.warn('매장 현황 조회 실패:', targetProjectCode, err);
        setStoreInfo({ count: 0, ACTIVE: 0, INACTIVE: 0 });
      });
  }, [projectCode, projectDetail.code]);
  
  useEffect(() => {
    const targetProjectCode = projectCode || projectDetail.code;
    if (!targetProjectCode) {
      setProductInfo({ count: 0, ACTIVE: 0, INACTIVE: 0 });
      return;
    }

    getProductStatusCount(targetProjectCode)
      .then((stats) => {
        setProductInfo(stats);
      })
      .catch((err) => {
        console.warn('상품 현황 조회 실패:', targetProjectCode, err);
        setProductInfo({ count: 0, ACTIVE: 0, INACTIVE: 0 });
      });
  }, [projectCode, projectDetail.code]);

  const membershipInfo: MembershipInfo = {
    totalMembers: 1250,
    activeMembers: 980,
    vipMembers: 120,
    monthlyGrowth: 12.5
  };

  const recentQna: QnaItem[] = [
    { id: 'QNA-001', title: '환불 처리 요청드립니다', createdAt: '2025-01-14 10:30', status: 'PENDING', priority: 'high' },
    { id: 'QNA-002', title: '포인트 적립이 안되었습니다', createdAt: '2025-01-14 09:15', status: 'PENDING', priority: 'medium' },
    { id: 'QNA-003', title: '멤버십 등급 문의', createdAt: '2025-01-13 18:20', status: 'PENDING', priority: 'low' },
    { id: 'QNA-004', title: '선물하기 기능 오류', createdAt: '2025-01-13 15:40', status: 'PENDING', priority: 'medium' }
  ];

  // Helper functions
  const getServiceIcon = (featureValue: string): React.ReactElement => {
    switch (featureValue) {
      case 'SHOWCASE':
        return <Utensils className="w-5 h-5" />;
      case 'MEMBERSHIP':
        return <Users className="w-5 h-5" />;
      case 'MOBILE_ORDER':
        return <Coffee className="w-5 h-5" />;
      case 'MALL':
        return <ShoppingBag className="w-5 h-5" />;
      default:
        return <Briefcase className="w-5 h-5" />;
    }
  };


  const getPriorityBadge = (priority: QnaItem['priority']): React.ReactElement => {
    switch (priority) {
      case 'high':
        return <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">긴급</span>;
      case 'medium':
        return <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">보통</span>;
      case 'low':
        return <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full">낮음</span>;
    }
  };


  return (
    <div className="h-screen bg-gray-100 flex overflow-hidden">
      <ProjectSidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        <Topbar
          title="프로젝트 대시보드"
          subtitle={`${formatProjectNameWithType(projectDetail.name, projectDetail.projectType)} (${projectDetail.code})`}
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
        />

        {/* Dashboard Content */}
        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Project Summary */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden mr-4">
                  {projectDetail.logo ? (
                    <img 
                      src={projectDetail.logo} 
                      alt={formatProjectNameWithType(projectDetail.name, projectDetail.projectType)}
                      className="w-full h-full object-cover rounded-lg"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  <div className={`text-4xl ${projectDetail.logo ? 'hidden' : ''}`}>📁</div>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {formatProjectNameWithType(projectDetail.name, projectDetail.projectType)}
                  </h2>
                  <p className="text-sm text-gray-500">{projectDetail.description}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-600 mb-1">프로젝트 코드</p>
                  <p className="font-medium text-gray-900">{projectDetail.code}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">상태</p>
                  <div className="mt-1">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <span className="w-1.5 h-1.5 mr-1 bg-green-400 rounded-full"></span>
                      활성
                    </span>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">생성일</p>
                  <p className="font-medium text-gray-900">{projectDetail.createdAt}</p>
                </div>
              </div>
            </div>

            {/* Service Status */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Activity className="w-5 h-5 mr-2" />
                서비스 현황
              </h2>
              {PROJECT_FEATURE_OPTIONS.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {PROJECT_FEATURE_OPTIONS.map((feature) => {
                    const isActive = featureCodes.includes(feature.value);
                    return (
                  <div key={feature.value} className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <div className="w-5 h-5 text-gray-600 mr-2">
                          {getServiceIcon(feature.value)}
                        </div>
                        <span className="font-medium text-gray-900">{getProjectFeatureLabel(feature.value)}</span>
                      </div>
                      <span className={`w-2 h-2 rounded-full ${
                        isActive ? 'bg-green-500' : 'bg-gray-400'
                      }`} />
                    </div>
                    <p className="text-xs text-gray-500">
                      {isActive ? `운영중 • ${projectDetail.createdAt}` : '준비중'}
                    </p>
                  </div>
                    );
                  })}
                </div>
              )}
              {featureCodes.length === 0 && (
                <p className="mt-4 text-sm text-gray-500">프로젝트 설정에서 사용 기능을 선택해 주세요.</p>
              )}
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Store Info */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-semibold text-gray-900">매장 현황</h3>
                  <Store className="w-5 h-5 text-gray-400" />
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">총 매장</span>
                    <span className="text-2xl font-bold text-gray-900">{storeInfo.count}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">운영중</span>
                    <span className="font-medium text-green-600">{storeInfo.ACTIVE}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">중지</span>
                    <span className="font-medium text-gray-500">{storeInfo.INACTIVE}</span>
                  </div>
                </div>
              </div>

              {/* Product Info */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-semibold text-gray-900">상품 현황</h3>
                  <Package className="w-5 h-5 text-gray-400" />
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">총 상품</span>
                    <span className="text-2xl font-bold text-gray-900">{productInfo.count}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">판매중</span>
                    <span className="font-medium text-green-600">{productInfo.ACTIVE}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">판매중지</span>
                    <span className="font-medium text-gray-500">{productInfo.INACTIVE}</span>
                  </div>
                </div>
              </div>

              {/* Membership Info */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-semibold text-gray-900">멤버십 현황</h3>
                  <Users className="w-5 h-5 text-gray-400" />
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">총 회원</span>
                    <span className="text-2xl font-bold text-gray-900">{membershipInfo.totalMembers.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">활성 회원</span>
                    <span className="font-medium text-green-600">{membershipInfo.activeMembers.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">월 증가율</span>
                    <span className="font-medium text-blue-600 flex items-center">
                      <ArrowUp className="w-3 h-3 mr-1" />
                      {membershipInfo.monthlyGrowth}%
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent QnA */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <MessageSquare className="w-5 h-5 mr-2" />
                  미답변 1:1 문의
                </h2>
                <button className="text-sm text-blue-600 hover:underline">
                  전체보기
                </button>
              </div>
              <div className="space-y-3">
                {recentQna.map((qna) => (
                  <div key={qna.id} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <p className="font-medium text-gray-900 text-sm">{qna.title}</p>
                        {getPriorityBadge(qna.priority)}
                      </div>
                      <p className="text-xs text-gray-500">{qna.id} • {qna.createdAt}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400 mt-1" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ProjectDashboard;